import { ReactNode } from "react";
import { cn } from "@/lib/utils";
import { PageContainer } from "@/components/flowly/PageContainer/PageContainer";
import { StudioHeader } from "@/components/flowly/StudioHeader/StudioHeader";
import { useLanguage } from "@/contexts/LanguageContext";
import type { SeoKey } from "@/config/seo";

interface StudioPageLayoutProps {
  activeTab: "images" | "marketing" | "videos" | "trends";
  activeApp?: "imageGenerator" | "ads" | "director" | "profilePhoto" | "productCreative" | "framesToVideo" | "textToVideo" | "talkingAvatar" | "baseballTrend";
  headerChildren?: ReactNode;
  banner?: ReactNode;
  children: ReactNode;
  contentClassName?: string;
  footer?: ReactNode;
  lightbox?: ReactNode;
  overlays?: ReactNode;
  /**
   * For hero-less flows only: renders a visually-hidden <h1> (the page's SEO
   * heading) so the page has exactly one h1. Flows that render their own hero
   * heading omit this.
   */
  seoHeadingKey?: SeoKey;
}

export function StudioPageLayout({
  activeTab,
  activeApp,
  headerChildren,
  banner,
  children,
  contentClassName,
  footer,
  lightbox,
  overlays,
  seoHeadingKey,
}: StudioPageLayoutProps) {
  const { t } = useLanguage();

  return (
    <div className="min-h-screen flex flex-col">
      {seoHeadingKey && (
        <h1 className="sr-only">{t.seo[seoHeadingKey].title}</h1>
      )}
      <StudioHeader activeTab={activeTab} activeApp={activeApp}>
        {headerChildren}
      </StudioHeader>

      {banner}

      <PageContainer
        width="wide"
        className={cn("flex-1 flex flex-col pb-36 md:pb-32", contentClassName)}
      >
        {children}
      </PageContainer>

      {footer}

      {lightbox}

      {overlays}
    </div>
  );
}
